import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import { Camera, Save, AlertCircle, CheckCircle, Award } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import axios from 'axios';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

export default function MemberProfile() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [saved, setSaved] = useState(false);
  const photoInput = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    fetchApplication();
  }, [user, authLoading, navigate]);

  const fetchApplication = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/membership-applications/my-application`, {
        headers: authHeaders(), withCredentials: true
      });
      setApplication(data);
      setForm({
        name: data.name || '',
        phone: data.phone || '',
        bio: data.bio || '',
        designation: data.designation || '',
        organization_affiliation: data.organization_affiliation || '',
        linkedin_profile: data.linkedin_profile || '',
        organization_type: data.organization_type || '',
        website_url: data.website_url || '',
        representing_executive_name: data.representing_executive_name || '',
        representing_executive_designation: data.representing_executive_designation || '',
        num_employees: data.num_employees || '',
        nature_of_business: data.nature_of_business || '',
        photo_url: data.photo_url || '',
        logo_url: data.logo_url || ''
      });
    } catch (error) {
      if (error.response?.status === 404) setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const set = (field) => (e) => { setForm({ ...form, [field]: e.target.value }); setSaved(false); };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API_BASE_URL}/api/membership-applications/upload-photo`, fd);
      const key = application.member_type === 'institutional' ? 'logo_url' : 'photo_url';
      setForm(prev => ({ ...prev, [key]: data.url }));
      setSaved(false);
    } catch (error) {
      alert(error.response?.data?.detail || 'Photo upload failed');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleCertificateDownload = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/membership-applications/my-certificate`, {
        headers: authHeaders(), withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'CSED_Membership_Certificate.pdf';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 100);
    } catch (error) {
      alert('Certificate download failed. It is available once your membership is approved.');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/api/membership-applications/my-application`, form, {
        headers: authHeaders(), withCredentials: true
      });
      setSaved(true);
      await fetchApplication();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-grow flex items-center justify-center pt-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E87722]"></div>
        </main>
        <Footer />
      </div>
    );
  }

  const isInstitutional = application?.member_type === 'institutional';
  const currentImage = isInstitutional ? form.logo_url : form.photo_url;

  const renderField = (label, field, placeholder = '', type = 'text') => (
    <div key={field}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <Input type={type} value={form[field] || ''} onChange={set(field)} placeholder={placeholder}
        data-testid={`profile-field-${field}`} />
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#1E3A5F]/90 pt-32 pb-8">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl font-bold text-white" data-testid="member-profile-title">My Profile</h1>
          <p className="text-gray-200 mt-2">Keep your member profile up to date — this is what other members see in the directory</p>
        </div>
      </div>

      <main className="flex-grow">
        <div className="container mx-auto px-4 py-10 max-w-3xl">
          {notFound ? (
            <Card className="p-10 text-center" data-testid="profile-no-application">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-[#1E3A5F] mb-2">No Membership Found</h2>
              <p className="text-gray-600 mb-6">We couldn't find a membership linked to <strong>{user?.email}</strong>.</p>
              <Button onClick={() => navigate('/membership')} className="bg-[#E87722] hover:bg-[#d66a1a]">
                View Membership Plans
              </Button>
            </Card>
          ) : (
            <>
              {/* Photo / Logo */}
              <Card className="p-6 mb-6">
                <div className="flex items-center gap-6">
                  <div className="relative">
                    {currentImage ? (
                      <img src={currentImage} alt="Profile" className="h-24 w-24 rounded-full object-cover border-4 border-[#E87722]/30" data-testid="profile-image" />
                    ) : (
                      <div className="h-24 w-24 rounded-full bg-[#1E3A5F] text-white flex items-center justify-center font-bold text-2xl">
                        {(form.name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
                      </div>
                    )}
                    <button
                      onClick={() => photoInput.current?.click()}
                      className="absolute -bottom-1 -right-1 bg-[#E87722] text-white p-2 rounded-full hover:bg-[#d66a1a] transition-colors"
                      title={isInstitutional ? 'Change logo' : 'Change photo'}
                      data-testid="profile-photo-btn"
                    >
                      <Camera className="h-4 w-4" />
                    </button>
                    <input type="file" accept=".jpg,.jpeg,.png" className="hidden" ref={photoInput}
                      onChange={(e) => handlePhotoUpload(e.target.files[0])} data-testid="profile-photo-input" />
                  </div>
                  <div>
                    <h2 className="font-bold text-lg text-[#1E3A5F]">
                      {isInstitutional ? (application.organization_name || application.name) : application.name}
                    </h2>
                    <p className="text-sm text-gray-500">{application.email}</p>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline">{application.selected_plan}</Badge>
                      {application.status === 'approved' && (
                        <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Verified</Badge>
                      )}
                    </div>
                    {uploadingPhoto && <p className="text-xs text-[#E87722] mt-2">Uploading...</p>}
                  </div>
                </div>
              </Card>

              {/* Details */}
              <Card className="p-6 mb-6">
                <h3 className="font-bold text-[#1E3A5F] mb-4">Details</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {renderField(isInstitutional ? 'Contact Person Name' : 'Full Name', 'name')}
                  {renderField('Phone', 'phone', '+91...')}
                  {isInstitutional ? (
                    <>
                      {renderField('Organisation Type', 'organization_type', 'NGO / Pvt Ltd / ...')}
                      {renderField('Website', 'website_url', 'https://...')}
                      {renderField('Representing Executive', 'representing_executive_name')}
                      {renderField('Executive Designation', 'representing_executive_designation')}
                      {renderField('Number of Employees', 'num_employees')}
                      {renderField('Nature of Business', 'nature_of_business')}
                    </>
                  ) : (
                    <>
                      {renderField('Designation', 'designation')}
                      {renderField('Organisation / Affiliation', 'organization_affiliation')}
                      {renderField('LinkedIn Profile', 'linkedin_profile', 'https://linkedin.com/in/...')}
                    </>
                  )}
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    About {isInstitutional ? 'your organisation' : 'you'} (Bio)
                  </label>
                  <Textarea
                    value={form.bio || ''}
                    onChange={set('bio')}
                    maxLength={600}
                    rows={4}
                    placeholder={isInstitutional
                      ? 'Describe your organisation, mission and impact areas...'
                      : 'Share your expertise, interests and what you bring to the network...'}
                    data-testid="profile-bio-input"
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">{(form.bio || '').length}/600</p>
                </div>
              </Card>

              <div className="flex items-center gap-4 flex-wrap">
                <Button onClick={handleSave} disabled={saving} className="bg-[#E87722] hover:bg-[#d66a1a]" data-testid="profile-save-btn">
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Profile'}
                </Button>
                {application?.status === 'approved' && (
                  <Button variant="outline" onClick={handleCertificateDownload}
                    className="border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white"
                    data-testid="download-certificate-btn">
                    <Award className="h-4 w-4 mr-2" />
                    Download Membership Certificate
                  </Button>
                )}
                {saved && (
                  <span className="text-green-600 text-sm flex items-center gap-1" data-testid="profile-saved-msg">
                    <CheckCircle className="h-4 w-4" />Profile saved
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
